<template>
  <article :class="classes" class="sign-up">
    <page-section
      v-if="intro"
      :title="intro.title"
      class="sign-up__hero mt-y-gutter-sm"
    >
      <template #header>
        <sanity-blocks
          v-if="intro.text"
          :blocks="intro.text"
          class="mt-1/2em"
        />
      </template>
      <div class="container">
        <client-only>
          <ValidationObserver
            ref="form"
            v-slot="{ errors }"
            tag="form"
            class="lowercase sign-up__form heading--sm"
            novalidate
            @submit.prevent="submit"
          >
            <fade>
              <div
                v-if="state !== 'success'"
                class="relative md:mb-y-gutter-sm"
              >
                <div class="relative flex flex-no-wrap items-center">
                  <ValidationProvider
                    v-slot="{ errors: nameErrors }"
                    name="name"
                    tag="div"
                    class="flex-auto pr-2 md:pr-4"
                  >
                    <div class="relative w-full">
                      <fade>
                        <label
                          v-if="!focused('name') && !name"
                          :for="`name-${$_uid}-input`"
                          class="absolute left-0 transform -translate-y-1/2 sign-up__label top-1/2"
                          >Name</label
                        >
                      </fade>
                      <input
                        :id="`name-${$_uid}-input`"
                        v-model="name"
                        type="text"
                        :class="{ 'is-error': nameErrors.length > 0 }"
                        class="block w-full sign-up__input"
                        @focus="focus('name')"
                        @blur="blur('name')"
                      />
                      <fade appear>
                        <ul
                          v-if="bp && bp.md && nameErrors.length > 0"
                          class="absolute left-0 hidden w-full sign-up__errors md:block"
                        >
                          <li
                            v-for="(error, i) in nameErrors"
                            :key="`error-${i}`"
                            class="text-xs text-red mt-1/2em"
                          >
                            {{ error }}
                          </li>
                        </ul>
                      </fade>
                    </div>
                  </ValidationProvider>
                  <ValidationProvider
                    v-slot="{ errors: emailErrors }"
                    name="email"
                    tag="div"
                    rules="required|email"
                    class="flex-auto pl-2 md:pl-4"
                  >
                    <div class="relative w-full">
                      <fade>
                        <label
                          v-if="!focused('email') && !email"
                          :for="`email-${$_uid}-input`"
                          class="absolute left-0 transform -translate-y-1/2 sign-up__label top-1/2"
                          >Email</label
                        >
                      </fade>
                      <input
                        :id="`email-${$_uid}-input`"
                        v-model="email"
                        type="email"
                        :class="{ 'is-error': emailErrors.length > 0 }"
                        class="block w-full sign-up__input"
                        @focus="focus('email')"
                        @blur="blur('email')"
                      />
                      <fade appear>
                        <ul
                          v-if="bp && bp.md && emailErrors.length > 0"
                          class="absolute left-0 hidden w-full sign-up__errors md:block"
                        >
                          <li
                            v-for="(error, i) in emailErrors"
                            :key="`error-${i}`"
                            class="text-xs text-red mt-1/2em"
                          >
                            {{ error }}
                          </li>
                        </ul>
                      </fade>
                    </div>
                  </ValidationProvider>
                </div>
                <fade appear>
                  <div
                    v-if="bp && bp.md && serverError"
                    class="absolute left-0 hidden w-full text-xs text-red mt-1/2em md:block"
                  >
                    {{ serverError }}
                  </div>
                </fade>
                <fade appear>
                  <ul
                    v-if="
                      bp &&
                        !bp.md &&
                        ((errors && Object.values(errors).flat().length) ||
                          serverError)
                    "
                    class="w-full sign-up__errors md:hidden"
                  >
                    <li
                      v-for="(error, i) in Object.values(errors).flat()"
                      :key="`error-${i}`"
                      class="inline-block text-xs text-red mt-1/2em mr-1/2em"
                    >
                      {{ error }}
                    </li>
                    <li v-if="serverError">
                      {{ serverError }}
                    </li>
                  </ul>
                </fade>
              </div>
            </fade>
            <div
              class="text-xl text-center mt-y-gutter-sm md:mt-y-gutter xl:text-2xl"
            >
              <cta
                class="lowercase sign-up__button"
                :background-color="getColor('pink')"
                :shadow-color="getColor('cloud-burst')"
                fill
                type="submit"
              >
                <flipper :value="buttonText" />
              </cta>
            </div>
          </ValidationObserver>
        </client-only>
      </div>
      <div v-if="intro.disclaimer" class="container">
        <div
          class="mx-auto text-sm text-center mt-y-gutter max-w-200 sm:w-3/4 md:mt-y-gutter-lg 2xl:text-base"
        >
          <sanity-blocks :blocks="intro.disclaimer" />
        </div>
      </div>
      <div class="container container--narrow mt-y-gutter-sm">
        <sanity-media v-if="splash" v-bind="splash" />
      </div>
    </page-section>
    <page-section
      v-if="whatYouGet && whatYouGet.length > 0"
      :title="whatYouGetTitle"
      class="bg-pink text-cloud-burst mt-header-height"
    >
      <gradient
        :colors="[getTransparentColor('pink'), getColor('pink')]"
        force-gradient
        class="color-section__gradient color-section__gradient--top"
      />
      <div v-if="bp" class="pb-header-height">
        <div
          v-if="bp.sm"
          class="flex flex-wrap justify-center text-center wrap"
        >
          <figure
            v-for="(offer, i) in whatYouGet"
            :key="`offer-${i}`"
            class="flex flex-col items-center flex-grow-0 flex-shrink-0 w-1/4 px-6 sign-up__slide min-w-64 pb-y-gutter-sm"
          >
            <sanity-media
              v-if="offer.thumbnail"
              :image="offer.thumbnail"
              class="sign-up__slide-image"
            />
            <figcaption v-if="offer.heading1 || offer.heading2">
              <h3
                v-if="offer.heading1"
                class="font-medium uppercase heading--xs"
              >
                {{ offer.heading1 }}
              </h3>
              <h4 v-if="offer.heading2" class="leading-snug mt-3/4em">
                {{ offer.heading2 }}
              </h4>
            </figcaption>
          </figure>
        </div>
        <gallery
          v-else
          :slides="whatYouGet"
          :options="{
            autoplay: {
              delay: 3000
            },
            effect: 'fade',
            fadeEffect: {
              crossFade: true
            }
          }"
          class="text-center wrap"
        >
          <figure
            v-for="(offer, i) in whatYouGet"
            :key="`offer-${i}`"
            class="sign-up__slide swiper-slide pb-y-gutter-sm"
          >
            <sanity-media
              v-if="offer.thumbnail"
              :image="offer.thumbnail"
              class="mx-auto sign-up__slide-image max-w-3/4"
            />
            <figcaption v-if="offer.heading1 || offer.heading2">
              <h3
                v-if="offer.heading1"
                class="font-medium uppercase heading--xs"
              >
                {{ offer.heading1 }}
              </h3>
              <h4 v-if="offer.heading2" class="mt-1/2em">
                {{ offer.heading2 }}
              </h4>
            </figcaption>
          </figure>
        </gallery>
      </div>
    </page-section>
  </article>
</template>

<script>
import wait from 'waait'
import { mapState, mapGetters } from 'vuex'
import page from '~/mixins/page'
import sanityQuery from '~/sanity/queries/sign-up'
import signupMutation from '~/graphql/mutations/signup.gql'
import preventOrphanedWord from '~/util/text/prevent-orphaned-word'

export default {
  mixins: [page],

  async asyncData({ route, params, query, store, $sanity, error }) {
    const slug = params.page
    const previewId = query.preview
    try {
      const signUp = await $sanity.fetch(sanityQuery(slug, previewId))
      if (!signUp) {
        throw new Error('Page not found')
      }
      return signUp
    } catch (e) {
      return error({
        statusCode: 404,
        message: e.message
      })
    }
  },

  data() {
    return {
      title: undefined,
      intro: undefined,
      listId: undefined,
      whatYouGetTitle: "why it's great",
      whatYouGet: [],
      splash: undefined,
      name: '',
      email: '',
      state: 'initial',
      serverError: undefined,
      focusedField: null
    }
  },

  computed: {
    ...mapState({
      googleClientId: state => state.googleClientId,
      headerHeight: state => state.screen.headerHeight
    }),
    ...mapGetters({
      bp: 'screen/breakpoint'
    }),
    classes() {
      return {
        'has-splash-image': !!this.splashImage
      }
    },
    titleHtml() {
      const title = this.title
      if (!title) {
        return
      }
      return preventOrphanedWord(title)
    },
    buttonText() {
      const intro = this.intro
      switch (this.state) {
        case 'submitting':
          return 'sending'
        case 'submitted':
          return 'submitted'
        case 'error':
          return 'oops'
        case 'success':
          return 'thank you!'
        default:
          return (intro && intro.cta) || 'submit'
      }
    }
  },

  methods: {
    focused(field) {
      return field === this.focusedField
    },
    focus(field) {
      this.focusedField = field
    },
    blur(field) {
      const focused = this.focusedField
      if (field === focused) {
        this.focusedField = null
      }
    },
    async submit() {
      if (this.state !== 'initial') {
        return
      }
      const name = this.name
      const email = this.email
      const listId = this.listId
      const valid = await this.$refs.form.validate()
      if (!valid) {
        this.state = 'error'
        await wait(3000)
        if (this.state !== 'initial') {
          this.state = 'initial'
        }
        return
      }
      this.state = 'submitting'
      try {
        this.$gtm.push({
          event: 'Lead',
          google_client_id: this.googleClientId,
          email
        })

        var klaviyo = window.klaviyo || [] // eslint-disable-line
        klaviyo.push([
          'identify',
          {
            $name: name,
            $email: email
          }
        ])
        // show 'submitting' text for at least a beat
        await wait(1000)
        try {
          await this.$apollo.mutate({
            mutation: signupMutation,
            variables: { email, listId, properties: { name } }
          })
        } catch (e) {
          throw new Error(e)
        }
        this.state = 'submitted'
        await wait(2000)
        this.name = ''
        this.email = ''
        requestAnimationFrame(() => {
          this.$refs.form.reset()
        })
        this.$emit('success')
      } catch (e) {
        this.state = 'error'
        this.serverError = e.message
        // eslint-disable-next-line no-console
        console.error(e)
      }
      this.state = 'success'
    }
  }
}
</script>

<style lang="scss">
.page--sign-up-page .page {
  padding-bottom: 0;
  padding-top: 0;
}

.sign-up__form {
  margin-left: auto;
  margin-right: auto;
  max-width: 720px;
}

.sign-up__window {
  flex: 0 1 auto;
  position: relative;
  width: 100%;
}

.sign-up__errors {
  padding-top: 0.5rem;
}

.sign-up__label {
  left: 1rem;
}

.sign-up__input:focus {
  outline: 0;
}

.sign-up__input {
  border-bottom: 1.5px solid currentColor;
  display: block;
  max-height: 3.5em;
  padding: 0.5rem 1rem;
  transition: border-color var(--hover-speed);
}

.sign-up__input.is-error {
  border-color: theme('colors.red');
}

.sign-up__slide-image {
  padding-top: 100% !important;
  width: 100%;
}

.sign-up__slide-image::before {
  content: '';
  display: block;
  padding-top: 100%;
}

.sign-up__slide-image .image__img {
  bottom: 0;
  left: 0;
  object-fit: contain;
  object-position: center;
  position: absolute;
  right: 0;
  top: 0;
}

@screen lg {
  .sign-up__form {
    max-width: 800px;
  }
}

@screen xl {
  .sign-up__form {
    max-width: 860px;
  }
}
</style>
